'use client'
import { useCallback, useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { toast } from 'react-toastify';

const LoginPage = () => {
    const [phoneNumber, setPhoneNumber] = useState('');
    const [password1, setPassword1] = useState('');
    const [password2, setPassword2] = useState('');
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [timer, setTimer] = useState<number>(900); // 15 minutes in seconds
    const [otpSent, setOtpSent] = useState<boolean>(false); // Flag to check if OTP was sent
    const router = useRouter();

    const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
        let interval: number | undefined;
    
        if (step === 2 && otpSent && timer > 0) {
            interval = window.setInterval(() => {
                setTimer(prev => {
                    if (prev <= 1) {
                        window.clearInterval(interval);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
    
        return () => {
            if (interval) {
                window.clearInterval(interval);
            }
        };
    }, [step, timer, otpSent]);


    const clearOtpInput = () => {
        setOtp(['', '', '', '', '', ''])
    }

    const handleOtpChange = (value: string, index: number) => {
        if (value.length > 1) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        if (value && index < 5) {
            otpRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>, index: number) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('Text');
        const numericOnlyString = pastedData.replace(/\D/g, '');
        const otpString = numericOnlyString.slice(0, 6);
        const otpArray = otpString.split('').slice(0, 6); // Limit to 6 characters
        const newOtp = [...otp];
        otpArray.forEach((char, i) => {
            if (i < 6) newOtp[i] = char;
        });
        setOtp(newOtp);

        otpRefs.current[otpArray.length < 6 ? otpArray.length : 5]?.focus();
    };

    const setRef = useCallback((index: number) => (el: HTMLInputElement | null) => {
        otpRefs.current[index] = el;
    }, []);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
        if (e.key === 'Enter') {
            handleVerifyOtpAndRegister();
        }
    };

    const handleInput = (e: React.FormEvent<HTMLInputElement>, index: number) => {
        const value = (e.target as HTMLInputElement).value;
        handleOtpChange(value, index);
    };

    const handleSendOtp = async () => {
        if (!phoneNumber || !password1 || !password2) {
            toast.error('Бүх талбарыг бөглөнө үү.');
            return;
        }

        if (password1.length < 8) {
            toast.error('Нууц үг 8-аас доошгүй тэмдэгттэй байх ёстой.');
            return;
        }

        if (password1 !== password2) {
            toast.error('Нууц үг зөрж байна');
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${process.env.NEXT_PUBLIC_API_URL}/api/register/`, { phone_number: phoneNumber });
            setStep(2);
            setOtpSent(true);
            setTimer(300);
            toast.success('Таны утасны дугаарт кодыг илгээлээ.');
        } catch (error) {
            if (axios.isAxiosError(error)) {
                if (error.response?.status === 400) {
                    toast.error(error.response.data.error);
                }
            } else {
                toast.error('Сүлжээний алдаа. Та дараа дахин оролдоно уу.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResendOtp = async () => {
        if (!phoneNumber) {
            toast.error('Утасны дугаар дахин оруулна уу.');
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${process.env.NEXT_PUBLIC_API_URL}/api/register/`, { phone_number: phoneNumber });
            setTimer(300); // Reset timer to 15 minutes
            toast.success('Шинэ код илгээлээ.');
        } catch (error) {
            if (axios.isAxiosError(error)) {
                if (error.response?.status === 400) {
                    toast.error(error.response.data.error);
                }
            } else {
                toast.error('Сүлжээний алдаа. Та дараа дахин оролдоно уу.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtpAndRegister = async () => {
        const fullOtp = otp.join('');
        if (fullOtp.length !== 6) {
            toast.error('Бүрэн бөглөнө үү.');
            clearOtpInput();
            otpRefs.current[0]?.focus();
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${process.env.NEXT_PUBLIC_API_URL}/api/verify-registration/`, {
                phone_number: phoneNumber,
                password: password1,
                otp: fullOtp,
            });
            toast.success('Амжилттай бүртгүүллээ');
            router.push('/login');
        } catch (error) {
            if (axios.isAxiosError(error)) {
                if (error.response?.status === 400) {
                    toast.error(error.response.data.error);
                }
            } else {
                toast.error('Сүлжээний алдаа. Та дараа дахин оролдоно уу.');
            }
            clearOtpInput();
            otpRefs.current[0]?.focus();
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDownForForm = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSendOtp();
        }
    };

    const formatTime = (seconds: number) => {
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    };

    return (
        <div className='h-screen w-screen bg-white md:bg-purple-50 dark:md:bg-dark-800 dark:bg-dark-600'>
            <div className="flex flex-col justify-center items-center h-screen">
                <div className='p-14'>
                    <Link href="/">
                        <div>
                            <Image
                                src="/logo-black-vector.svg"
                                alt="Logo Light Mode"
                                className="dark:hidden"
                                width={260}
                                height={30}
                                priority
                            />
                            <Image
                                src="/logo-white-vector.svg"
                                alt="Logo Dark Mode"
                                className="hidden dark:block"
                                width={260}
                                height={60}
                                priority
                            />
                        </div>
                    </Link>
                </div>

                <div className="w-full max-w-md p-8 bg-white md:shadow-md md:rounded-xl space-y-5 dark:bg-dark-600">
                    {step === 1 && (
                        <>
                            <h1 className="text-2xl font-bold mb-4 text-center">Та утасны дугаараа оруулна уу</h1>

                            <input
                                type="number"
                                placeholder="Утасны дугаар"
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value)}
                                className="w-full p-4 border border-gray-300 rounded-lg mb-4 text-dark-700"
                                onKeyDown={handleKeyDownForForm}
                            />
                            <input
                                type="password"
                                placeholder="Нууц үг"
                                value={password1}
                                onChange={(e) => setPassword1(e.target.value)}
                                className="w-full p-4 border border-gray-300 rounded-lg mb-4 text-dark-700"
                                onKeyDown={handleKeyDownForForm}
                            />
                            <input
                                type="password"
                                placeholder="Нууц үг баталгаажуулах"
                                value={password2}
                                onChange={(e) => setPassword2(e.target.value)}
                                className="w-full p-4 border border-gray-300 rounded-lg mb-4 text-dark-700"
                                onKeyDown={handleKeyDownForForm}
                            />
                            <button
                                onClick={handleSendOtp}
                                className=" w-full font-bold p-4 text-md bg-purple-500 text-white rounded-lg flex items-center justify-center"
                                disabled={loading}
                            >
                                {loading ? (
                                    <div className="w-6 h-6 border-4 border-t-4 border-white border-solid rounded-full animate-spin"></div>
                                ) : (
                                    'Код илгээх'
                                )}
                            </button>
                        </>
                    )}

                    {step === 2 && (
                        <>
                            <h1 className="text-2xl font-bold mb-4 text-center">Таны {phoneNumber} утасны дугаарт ирсэн кодыг оруулна уу</h1>
                            <div className="flex justify-center space-x-2">
                                {otp.map((value, index) => (
                                    <input
                                        key={index}
                                        type="number"
                                        value={value}
                                        onInput={(e) => handleInput(e, index)}
                                        onKeyDown={(e) => handleKeyDown(e, index)}
                                        onPaste={(e) => handleOtpPaste(e, index)}
                                        ref={setRef(index)}
                                        maxLength={1}
                                        className="w-12 h-12 text-center text-lg font-bold border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 text-dark-700"
                                    />
                                ))}
                            </div>
                            <div className='text-xs text-center'>
                                <button className='underline' onClick={clearOtpInput}>Цэвэрлэх</button>
                            </div>
                            <div className="text-center text-lg font-bold mb-2">
                                {timer > 0 ? (
                                    `Үлдсэн хугацаа: ${formatTime(timer)}`
                                ) : (
                                    'Хугацаа дууссан. Та дахин кодоо авна уу'
                                )}
                            </div>
                            {timer < 1 &&
                            <button
                                onClick={handleResendOtp}
                                className=" w-full font-bold p-4 text-md bg-purple-500 text-white rounded-lg hover:bg-purple-600 flex items-center justify-center"
                                disabled={loading || timer > 0}
                            >
                                {loading ? (
                                    <div className="w-6 h-6 border-4 border-t-4 border-white border-solid rounded-full animate-spin"></div>
                                ) : (
                                    'Код дахин илгээх'
                                )}
                            </button>
                            }
                            {timer > 0 &&
                            <button
                                onClick={handleVerifyOtpAndRegister}
                                className=" w-full font-bold p-4 text-md bg-purple-500 text-white rounded-lg hover:bg-purple-600 flex items-center justify-center"
                                disabled={loading}
                            >
                                {loading ? (
                                    <div className="w-6 h-6 border-4 border-t-4 border-white border-solid rounded-full animate-spin"></div>
                                ) : (
                                    'Бүртгүүлэх'
                                )}
                            </button>
                            }
                        </>
                    )}

                    <div className='mt-8 pt-5 border-t-2 border-gray-200 text-center text-sm'>
                        <div>
                            <span className='text-center font-semibold text-gray-600 dark:text-white'>Бүртгэлтэй?</span>
                            <Link href="/login">
                                <span className='ms-2 font-semibold text-center underline hover:cursor-pointer'>Нэвтрэх</span>
                            </Link>
                        </div>
                        <div className='mt-2'>
                            <span className='text-center font-semibold text-gray-600 dark:text-white'>Бүртгэлээ мартсан?</span>
                            <Link href="/recover">
                                <span className='ms-2 font-semibold text-center underline hover:cursor-pointer'>Сэргээх</span>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
